import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Card from '@/components/ui/Card'
import SectionIllustration from '@/components/SectionIllustration'
import HeroStories from '@/components/HeroStories'
import { supabase } from '@/supabase/client'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

interface HeroData {
  impact: { classrooms_launched: number; lockers_launched: number; total_donated_cents: number; sponsors_active: number };
  featured_nominee: { name: string; type: string; school_or_sport: string | null; weight: number; support_code: string } | null;
  latest_ceremony: { kind: string; winners: number; executed_at: string } | null;
  featured_story: { body: string; author_name: string; author_avatar: string | null } | null;
}

interface SiteSettings {
  hide_broadcast?: boolean;
  hero_title?: string;
  hero_subtitle?: string;
  hero_cta_label?: string;
  hero_cta_url?: string;
  hero_image_url?: string | null;
}

const DEFAULT_SETTINGS: SiteSettings = {
  hide_broadcast: false,
  hero_title: 'Celebrate the sponsors powering classrooms.',
  hero_subtitle:
    'Discover the companies and community members funding classroom experiences across Project Tower.',
  hero_cta_label: 'Learn more',
  hero_cta_url: '/programs',
  hero_image_url: null,
}

function AnimatedCounter({ value, label }: { value: number; label: string }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (value <= 0) return;
    const duration = 1200;
    const start = performance.now();
    const tick = (now: number) => {
      const pct = Math.min((now - start) / duration, 1);
      setDisplay(Math.round(pct * value));
      if (pct < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value]);

  return (
    <div className="text-center">
      <div className="text-3xl font-extrabold text-cocoa">{display.toLocaleString()}</div>
      <div className="text-xs text-cocoa/60 mt-1">{label}</div>
    </div>
  );
}

export default function Home() {
  const [settings, setSettings] = useState<SiteSettings | null>(null)
  const [heroImageFailed, setHeroImageFailed] = useState(false)
  const [heroData, setHeroData] = useState<HeroData | null>(null)

  useEffect(() => {
    let isActive = true
    async function loadSettings() {
      try {
        const { data, error } = await supabase.from('site_settings').select('*').eq('id', 'site').maybeSingle()
        if (!isActive) return
        if (error && error.code !== 'PGRST116') {
          throw error
        }
        setSettings(data as unknown as SiteSettings | null)
      } catch (err) {
        if (!isActive) return
        console.warn('[home] failed to load site settings', err)
        setSettings(null)
      }
    }

    loadSettings()
    return () => {
      isActive = false
    }
  }, [])

  // Fetch dynamic hero data
  useEffect(() => {
    fetch(`${CORE_API}/api/v1/public/hero`)
      .then((r) => r.json())
      .then((json) => { if (json.data) setHeroData(json.data); })
      .catch(() => {});
  }, []);

  const heroSettings = useMemo(
    () => ({
      hideBroadcast: settings?.hide_broadcast ?? DEFAULT_SETTINGS.hide_broadcast,
      title: settings?.hero_title ?? DEFAULT_SETTINGS.hero_title,
      subtitle: settings?.hero_subtitle ?? DEFAULT_SETTINGS.hero_subtitle,
      ctaLabel: settings?.hero_cta_label ?? DEFAULT_SETTINGS.hero_cta_label,
      ctaUrl: settings?.hero_cta_url ?? DEFAULT_SETTINGS.hero_cta_url,
      imageUrl: settings?.hero_image_url ?? DEFAULT_SETTINGS.hero_image_url,
    }),
    [settings],
  )

  useEffect(() => {
    setHeroImageFailed(false)
  }, [heroSettings.imageUrl])

  const showBroadcast = !heroSettings.hideBroadcast
  const heroCtaIsInternal = heroSettings.ctaUrl?.startsWith('/')
  const heroCtaProps = heroCtaIsInternal
    ? { to: heroSettings.ctaUrl }
    : { href: heroSettings.ctaUrl, target: '_blank', rel: 'noreferrer' }

  const heroVisual = heroSettings.imageUrl && !heroImageFailed ? (
    <Card className="relative overflow-hidden bg-gradient-to-br from-brick/10 via-sand to-mint/10">
      <div className="relative flex justify-center p-6">
        <img
          src={heroSettings.imageUrl}
          alt="Homepage hero"
          className="h-56 w-full max-w-md rounded-2xl object-cover"
          loading="lazy"
          onError={() => setHeroImageFailed(true)}
        />
      </div>
    </Card>
  ) : (
    <Card className="relative overflow-hidden bg-gradient-to-br from-brick/10 via-sand to-mint/10">
      <div className="relative flex justify-center p-6">
        <SectionIllustration name="kids-joy" className="h-56 w-56" />
      </div>
    </Card>
  )

  const heroFallback = (
    <section className="grid items-center gap-8 md:grid-cols-2">
      <div className="space-y-6">
        <h1 className="text-5xl font-extrabold leading-tight text-cocoa">{heroSettings.title}</h1>
        <p className="text-lg text-cocoa/70">{heroSettings.subtitle}</p>
        {heroSettings.ctaLabel && heroSettings.ctaUrl ? (
          <div className="flex flex-wrap gap-3">
            {heroCtaIsInternal ? (
              <Link className="btn" to={heroSettings.ctaUrl}>
                {heroSettings.ctaLabel}
              </Link>
            ) : (
              <a className="btn" href={heroSettings.ctaUrl} target="_blank" rel="noreferrer">
                {heroSettings.ctaLabel}
              </a>
            )}
          </div>
        ) : null}
      </div>
      {heroVisual}
    </section>
  )

  return (
    <div className="space-y-16">
      {/* Dynamic Hero — Impact + Featured Nominee + Latest Ceremony */}
      <section className="rounded-3xl border border-white/70 bg-gradient-to-r from-brick/10 via-sand to-mint/10 p-6 md:p-8 shadow-soft">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Left: Impact counters */}
          <div className="space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-cocoa/50">Our Impact</p>
              <h1 className="text-2xl md:text-3xl font-bold text-cocoa mt-1">Building Communities</h1>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <AnimatedCounter value={heroData?.impact.classrooms_launched ?? 0} label="Classrooms" />
              <AnimatedCounter value={heroData?.impact.lockers_launched ?? 0} label="Locker Rooms" />
              <AnimatedCounter value={heroData?.impact.sponsors_active ?? 0} label="Sponsors" />
              <AnimatedCounter value={Math.round((heroData?.impact.total_donated_cents ?? 0) / 100)} label="$ Raised" />
            </div>

            {heroData?.latest_ceremony && (
              <div className="rounded-xl bg-white/60 p-3 backdrop-blur">
                <p className="text-xs text-cocoa/50">Latest Draw</p>
                <p className="text-sm font-semibold text-cocoa">
                  {heroData.latest_ceremony.winners} new {heroData.latest_ceremony.kind === 'classroom' ? 'classroom' : 'locker room'}{heroData.latest_ceremony.winners > 1 ? 's' : ''} launched!
                </p>
                <Link to="/community" className="text-xs text-brick hover:underline mt-1 inline-block">See in community &rarr;</Link>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <Link className="rounded-xl bg-brick px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brickDark" to="/programs">
                Our Programs
              </Link>
              <Link className="rounded-xl border border-sand bg-white px-5 py-2.5 text-sm font-semibold text-cocoa shadow-soft transition hover:bg-shell" to="/sponsor-classroom/donate">
                Donate
              </Link>
            </div>
          </div>

          {/* Right: Featured nominee or story */}
          <div className="space-y-4">
            {heroData?.featured_nominee && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5">
                <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold">Featured Nominee</p>
                <h3 className="text-lg font-bold text-cocoa mt-1">{heroData.featured_nominee.name}</h3>
                {heroData.featured_nominee.school_or_sport && (
                  <p className="text-sm text-cocoa/60">{heroData.featured_nominee.school_or_sport}</p>
                )}
                <div className="flex items-center gap-3 mt-3">
                  <span className="text-sm text-amber-700 font-semibold">&hearts; {heroData.featured_nominee.weight} supporters</span>
                  <Link
                    to={`/support/${heroData.featured_nominee.support_code}`}
                    className="rounded-full bg-amber-500 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-600"
                  >
                    Support
                  </Link>
                </div>
                <Link to="/rally" className="text-xs text-amber-600 hover:underline mt-2 inline-block">See all on the Rally Board &rarr;</Link>
              </div>
            )}

            {heroData?.featured_story && (
              <div className="rounded-2xl border border-sand bg-white/80 p-5">
                <p className="text-xs uppercase tracking-wider text-cocoa/50 font-semibold">From the Community</p>
                <p className="mt-2 text-sm text-cocoa/80 italic">&ldquo;{heroData.featured_story.body}&rdquo;</p>
                <p className="mt-2 text-xs text-cocoa/60">&mdash; {heroData.featured_story.author_name}</p>
              </div>
            )}

            {!heroData?.featured_nominee && !heroData?.featured_story && heroVisual}
          </div>
        </div>
      </section>

      <HeroStories fallback={heroFallback} />

      <section className="grid gap-6 md:grid-cols-3">
        {[
          { t: 'Think', d: 'Curiosity and critical thinking at the center.' },
          { t: 'Build', d: 'Hands-on projects that turn ideas into outcomes.' },
          { t: 'Serve', d: 'Community impact that grows with each cohort.' },
        ].map(item => (
          <Card key={item.t} className="space-y-2 p-5">
            <div className="text-xl font-semibold text-cocoa">{item.t}</div>
            <p className="text-sm text-cocoa/70">{item.d}</p>
          </Card>
        ))}
      </section>

      <section className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-2xl font-semibold text-cocoa">Learn about the SBN</h2>
          <p className="text-cocoa/70">
            The Smart Block Network (SBN) is our connective layer for bringing programs, reality-tested knowledge, and
            agent-powered earning together.
          </p>
        </header>
        <Card className="grid items-center gap-4 p-5 md:grid-cols-[1.2fr,0.8fr]">
          <div className="space-y-3">
            <p className="text-sm text-cocoa/80">
              We pair imaginative educators with aligned partners, then remove friction with transparent funding, smart tools,
              and ongoing storytelling.
            </p>
            <ul className="space-y-1 pl-5 text-sm text-cocoa/80 list-disc">
              <li>"How it works" lays out the building blocks for each initiative</li>
              <li>Reality Check keeps program details grounded with a living fact lattice</li>
              <li>SBN Gigs let aligned agents contribute skills and earn alongside the work</li>
            </ul>
            <div className="flex flex-wrap gap-2 pt-2">
              <Link to="/programs" className="btn">
                Browse Programs
              </Link>
              <Link to="/sponsor-classroom/donate" className="btn border border-sand bg-white text-cocoa hover:bg-shell">
                Open Donations
              </Link>
            </div>
          </div>
          <div className="flex justify-center">
            <SectionIllustration name="network" className="h-48 w-48" />
          </div>
        </Card>
      </section>
    </div>
  )
}
